// ============================================================================
// ADMIN SECTORS — System admin CRUD for the client sector catalog
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  Briefcase, Plus, Pencil, Loader, AlertCircle, X, CheckCircle, ToggleLeft, ToggleRight, Check
} from 'lucide-react';
import {
  listAllSectors,
  createSector,
  updateSector,
  Sector
} from '../services/studio-bookings';

function SectorRow({
  sector, editingId, editName, saving, togglingId,
  onEdit, onSave, onCancel, onToggle, setEditName
}: {
  sector: Sector;
  editingId: string | 'new' | null;
  editName: string;
  saving: boolean;
  togglingId: string | null;
  onEdit: (s: Sector) => void;
  onSave: () => void;
  onCancel: () => void;
  onToggle: (s: Sector) => void;
  setEditName: (v: string) => void;
}) {
  const isEditing = editingId === sector.sectorId;
  const isToggling = togglingId === sector.sectorId;
  let toggleIcon: React.ReactNode;
  if (isToggling) toggleIcon = <Loader className="w-4 h-4 animate-spin text-slate-400" />;
  else if (sector.isActive) toggleIcon = <ToggleRight className="w-5 h-5" />;
  else toggleIcon = <ToggleLeft className="w-5 h-5" />;

  if (isEditing) {
    return (
      <tr className="bg-primary/5">
        <td colSpan={2} className="px-5 py-3">
          <div className="flex gap-3 items-start">
            <input
              autoFocus
              type="text"
              value={editName}
              onChange={e => setEditName(e.target.value)}
              placeholder="Sector name"
              className="flex-1 border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
            />
            <div className="flex gap-2 flex-shrink-0 pt-0.5">
              <button
                onClick={onSave}
                disabled={!editName.trim() || saving}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-primary text-white text-xs font-bold disabled:opacity-60 shadow-sm shadow-primary/20 hover:bg-primary/90 transition-all"
              >
                {saving ? <Loader className="w-3 h-3 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Save
              </button>
              <button
                onClick={onCancel}
                className="px-3 py-2 border border-[#e2e8f0] rounded-xl text-xs font-medium text-slate-500 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className={`hover:bg-slate-50/50 transition-colors ${!sector.isActive ? 'opacity-50' : ''}`}>
      <td className="px-5 py-3.5">
        <p className="text-sm font-semibold text-navy">{sector.name}</p>
      </td>
      <td className="px-5 py-3.5">
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={() => onEdit(sector)}
            className="p-1.5 rounded-lg text-slate-400 hover:text-navy hover:bg-slate-100 transition-colors"
            title="Edit"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onToggle(sector)}
            disabled={togglingId === sector.sectorId}
            className={`transition-colors ${sector.isActive ? 'text-primary hover:text-primary/80' : 'text-slate-400 hover:text-slate-600'}`}
            title={sector.isActive ? 'Deactivate' : 'Activate'}
          >
            {toggleIcon}
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function AdminSectors() {
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  function load() {
    setLoading(true);
    listAllSectors()
      .then(setSectors)
      .catch(() => setError('Failed to load sectors'))
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(null), 4000);
      return () => clearTimeout(t);
    }
  }, [success]);

  function startEdit(s: Sector) {
    setEditingId(s.sectorId);
    setEditName(s.name);
  }

  function startCreate() {
    setEditingId('new');
    setEditName('');
  }

  function cancelEdit() {
    setEditingId(null);
    setEditName('');
  }

  async function handleSave() {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      if (editingId === 'new') {
        await createSector(editName.trim());
        setSuccess('Sector created');
      } else {
        await updateSector(editingId!, { name: editName.trim() });
        setSuccess('Sector updated');
      }
      cancelEdit();
      load();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save sector');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive(s: Sector) {
    setTogglingId(s.sectorId);
    try {
      await updateSector(s.sectorId, { isActive: !s.isActive });
      setSectors(prev => prev.map(x => x.sectorId === s.sectorId ? { ...x, isActive: !x.isActive } : x));
    } catch {
      setError('Failed to update sector');
    } finally {
      setTogglingId(null);
    }
  }

  const active = sectors.filter(s => s.isActive);
  const inactive = sectors.filter(s => !s.isActive);

  const tableProps = {
    editingId, editName, saving, togglingId,
    onEdit: startEdit, onSave: handleSave, onCancel: cancelEdit, onToggle: handleToggleActive,
    setEditName
  };

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">
      <header className="flex items-center justify-between border-b border-slate-200 pb-6">
        <div className="flex items-center gap-4">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
            <Briefcase size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-navy">Sectors</h1>
            <p className="text-slate-500 text-sm font-medium">Manage the client sector catalog used on studio bookings</p>
          </div>
        </div>
        {editingId !== 'new' && (
          <button
            onClick={startCreate}
            className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm"
          >
            <Plus className="w-4 h-4" /> Add sector
          </button>
        )}
      </header>

      {error && (
        <div className="mb-5 p-4 rounded-xl bg-red-50 border border-red-200 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700 flex-1">{error}</p>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {success && (
        <div className="mb-5 p-4 rounded-xl bg-green-50 border border-green-200 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <p className="text-sm text-green-700 font-medium">{success}</p>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center h-40 items-center">
          <Loader className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : (
        <div className="flex flex-col gap-8">
          <section>
            <p className="text-[11px] font-bold text-primary tracking-widest uppercase mb-3">
              Active <span className="font-normal normal-case text-slate-400">({active.length})</span>
            </p>
            <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#e2e8f0] bg-slate-50/60">
                    <th className="px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Name</th>
                    <th className="px-5 py-3 w-24" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e2e8f0]">
                  {editingId === 'new' && (
                    <tr className="bg-primary/5">
                      <td colSpan={2} className="px-5 py-3">
                        <div className="flex gap-3 items-start">
                          <input
                            autoFocus
                            type="text"
                            value={editName}
                            onChange={e => setEditName(e.target.value)}
                            placeholder="Sector name"
                            className="flex-1 border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                          />
                          <div className="flex gap-2 flex-shrink-0 pt-0.5">
                            <button
                              onClick={handleSave}
                              disabled={!editName.trim() || saving}
                              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-primary text-white text-xs font-bold disabled:opacity-60 shadow-sm shadow-primary/20 hover:bg-primary/90 transition-all"
                            >
                              {saving ? <Loader className="w-3 h-3 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                              Save
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="px-3 py-2 border border-[#e2e8f0] rounded-xl text-xs font-medium text-slate-500 hover:bg-slate-50 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  {active.length === 0 && editingId !== 'new' ? (
                    <tr>
                      <td colSpan={2} className="px-5 py-8 text-center text-sm text-slate-400">
                        No active sectors
                      </td>
                    </tr>
                  ) : (
                    active.map(s => (
                      <SectorRow key={s.sectorId} sector={s} {...tableProps} />
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {inactive.length > 0 && (
            <section>
              <p className="text-[11px] font-bold text-primary tracking-widest uppercase mb-3">
                Inactive <span className="font-normal normal-case text-slate-400">({inactive.length})</span>
              </p>
              <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#e2e8f0] bg-slate-50/60">
                      <th className="px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Name</th>
                      <th className="px-5 py-3 w-24" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#e2e8f0]">
                    {inactive.map(s => (
                      <SectorRow key={s.sectorId} sector={s} {...tableProps} />
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>
      )}

      </div>
    </div>
  );
}
